import { useState, useEffect } from "react";
import { useAdminSettings, useUpdateSettings } from "../../hooks/useAdmin";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";

export default function AdminSettingsPage() {
  const { data: remote } = useAdminSettings() as any;
  const update = useUpdateSettings();
  const [saved, setSaved]   = useState(false);
  const [cfg, setCfg] = useState({
    surety_pct:"50", interest_rate:"8", max_loan_amount:"1000000",
    overdue_grace_days:"7", kyc_provider:"mock", payment_gateway:"mock",
    admin_charge:"2000", maintenance_fee:"1000", registration_fee: "2000",
    technology_fee:"2000", welfare:"3000"
  });

  useEffect(() => {
    if (remote) {
      console.log("Keys received from backend:", Object.keys(remote));
      console.log("Full remote object structure:", remote);
      setCfg(c => ({ ...c, ...remote })); 
      }
  }, [remote]);
  
  const set = (k:string) => (v:string) => setCfg(c => ({ ...c, [k]:v }));
  
  const save = async () => {
    await update.mutateAsync(cfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="max-w-2xl">
      <Card className="mb-4">
        <p className="text-base font-medium text-slate-800 mb-5">Loan engine rules</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Surety balance threshold: <span className="text-emerald-600 font-semibold">{cfg.surety_pct}%</span>
            </label>
            <input type="range" min={10} max={100} step={5} value={cfg.surety_pct}
              onChange={e => set("surety_pct")(e.target.value)} className="w-full"/>
            <p className="text-xs text-slate-400 mt-1">Each surety must hold ≥ this % of the loan amount</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Annual interest rate: <span className="text-emerald-600 font-semibold">{cfg.interest_rate}%</span>
            </label>
            <input type="range" min={1} max={30} step={0.5} value={cfg.interest_rate}
              onChange={e => set("interest_rate")(e.target.value)} className="w-full"/>
          </div>
          <Input label="Max loan amount (NGN)"        value={cfg.max_loan_amount}       onChange={set("max_loan_amount")}/>
          <Input label="Overdue grace period (days)" value={cfg.overdue_grace_days}    onChange={set("overdue_grace_days")}/>
        </div>
      </Card>

      <Card className="mb-4">
        <p className="text-base font-medium text-slate-800 mb-5">Integrations</p>
        <Select label="KYC provider" value={cfg.kyc_provider} onChange={set("kyc_provider")} options={[
          {value:"mock",     label:"Mock (dev)"},
          {value:"onfido",   label:"Onfido"},
          {value:"jumio",    label:"Jumio"},
          {value:"smile_id", label:"Smile Identity"},
        ]}/>
        <Select label="Payment gateway" value={cfg.payment_gateway} onChange={set("payment_gateway")} options={[
          {value:"mock",         label:"Mock (dev)"},
          {value:"stripe",       label:"Stripe"},
          {value:"paystack",     label:"Paystack"},
          {value:"flutterwave",  label:"Flutterwave"},
          {value:"momo",         label:"Mobile Money (MTN)"},
        ]}/>

        <div className="grid grid-cols-2 gap-4">          
          <Input label="Administrative Charge (NGN)"        value={cfg.admin_charge}       onChange={set("admin_charge")}/>
          <Input label="Maintenance Fee (NGN)" value={cfg.maintenance_fee} onChange={set("maintenance_fee")}/>          
          <Input label="Registration Fee (NGN)" value={cfg.registration_fee} onChange={set("registration_fee")}/>
          <Input label="Technology Fee (NGN)"        value={cfg.technology_fee}       onChange={set("technology_fee")}/>
          <Input label="Welfare (NGN)" value={cfg.welfare} onChange={set("welfare")}/>
        </div>
      </Card>

      <div className="flex gap-3">
        <Button onClick={save} size="lg" style={{flex:1}} className="justify-center" disabled={update.isPending}>
          {saved ? "✅ Saved!" : update.isPending ? "Saving…" : "Save configuration"}
        </Button>
        <Button variant="secondary" onClick={() => remote && setCfg(c=>({...c,...remote}))}>
          Reset
        </Button>
      </div>
    </div>
  );
}
